/**
 * Size tokens — border widths, component heights, and content max-widths, mirrored from Figma
 * Variables (collections "sizes/border", "sizes/height", "sizes/max-width").
 *
 * `sizes.border`   — stroke widths for borders, dividers, and focus rings.
 * `sizes.height`   — fixed component heights for inputs, buttons, chips, table rows, etc.
 * `sizes.maxWidth` — the point at which content stops growing and wraps or truncates. Two uses:
 *                    TEXT-BEARING / OVERLAY surfaces (Tooltip, Menu/dropdown, Popover panels,
 *                    Dialog), and COMPACT CONTROLS that truncate their own content rather than
 *                    grow (filter triggers — `xs`, designer 2026-08-27). Still NOT for structural
 *                    layout widths (Sidebar rail, Drawer, panel widths) — those stay per-use.
 *
 * These are absolute px values used directly in component `styleOverrides`.
 */
export const sizes = {
  border: {
    bd1: 1,
    bd2: 2,
    bd4: 4, // label tab bottom border — fills the corner-radius gap between label and input
  },
  height: {
    h4: 4, // thinnest bar/indicator track (ProgressBar size="sm") — Figma h-4
    h8: 8, // status dot (Chip statusDot) — small indicators
    h12: 12,
    h14: 14, // icon button small — icon size
    h16: 16,
    h18: 18, // checkbox / radio icon size
    h20: 20,
    h22: 22, // icon button small — container size
    h24: 24,
    h28: 28,
    h32: 32,
    h36: 36,
    h40: 40,
  },
  /**
   * Floor for a surface that would otherwise size itself to its anchor — the other half of
   * `maxWidth`, added 2026-09-21 (designer).
   *
   * The case that forced it: the inline `Menu` sizes to its trigger, so a filter labelled "Type"
   * opened a menu barely wider than the word, with rows narrower than their own comfortable
   * reading width.
   *
   * Grounded in a measurement rather than taste: a `MenuItem` spends `sp16` on each side (32px of
   * chrome), a `body1` label runs ~6.6px per character, and a checkbox row adds ~36px for the
   * control. A real menu of short options measured 156px wide, so `xs` sits just above it and
   * nudges exactly the menus that were too tight.
   *
   * **This is NOT a step-for-step mirror of `maxWidth`** — the two scales are independent and
   * `minWidth.sm` (240) is not `maxWidth.sm` (320). They share numbers where the jobs happen to
   * meet, not by construction.
   */
  minWidth: {
    xs: 160, // menu / dropdown floor — applied globally to `MuiMenu.paper`
    sm: 240, // rows carrying secondary text under the label
    md: 320, // wide pickers and panels
  },
  // Content / overlay max-width (t-shirt scale, matches the radius naming convention).
  maxWidth: {
    xs: 240, // Tooltip, compact popovers
    sm: 320, // Menu, dropdowns
    md: 480, // filter panels, forms in an overlay
    lg: 640, // dialogs, comfortable reading measure
    xl: 800, // wide / two-column dialogs and overlays
  },
} as const;

export type SizeTokens = typeof sizes;
